import { Button, Icon } from '@zenchef/ds-react'
import { Box, Flex, Stack, styled } from '@zenchef/styled-system/jsx'
import { Loader, Text } from '@zenchef/ui'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import { useContext, useEffect } from 'react'

import { MainLayout, TableBox, TableRowBox, Well } from '@/components'
import Hr from '@/components/redesign/common/Hr'
import InformationIcon from '@/components/redesign/common/InformationIcon'
import MoreLessInput from '@/components/redesign/common/MoreLessInput'
import { Reference } from '@/components/Tooltip/Reference'
import { Tooltip } from '@/components/Tooltip/Tooltip'
import { Bookings, NextPageWithLayout } from '@/types/types'
import BodyPortal from '@/utils/BodyPortal'
import durationInWords from '@/utils/durationInWords'
import getDateFnsLocal from '@/utils/getDateFnsLocal'
import { useTooltip, useTranslation } from '@/utils/hooks'
import safelySetInnerHTML from '@/utils/safelySetInnerHTML'
import StoresContext from '@/utils/StoresContext'

const TooltipBox = styled(Box, {
  base: {
    width: '100%',
    p: 'padding.3',
    color: 'white',
    bg: 'background.neutral.base.bold',
    borderRadius: 'l',
    boxShadow: 'effect.shadow.bold-bottom'
  }
})

interface ProductProps {
  product: Bookings.Product
  index: number
  hoverProduct: (productId: number) => void
  unhoverProduct: () => void
  hoveringProduct: number | null
  addToCart: (productId: number) => void
}

const Product = observer(
  ({ product, index, hoverProduct, unhoverProduct, hoveringProduct, addToCart }: ProductProps) => {
    const { appStore } = useContext(StoresContext)
    const { t } = useTranslation()
    const { products, productUnitPriceFormatted } = appStore.state

    const { reference, popper } = useTooltip()

    const isHovering = hoveringProduct === product.id
    const locale = getDateFnsLocal(appStore.state.language)

    const isAvailable = true
    const isLast = index === products.length - 1
    return (
      <Well key={`product${product.id}`} mb={isLast ? 0 : '10px'} flexWrap={['wrap', 'nowrap']}>
        <Box p='10px'>
          <Text
            css='display:flex;align-items:center;'
            fontSize='m'
            fontWeight='bold'
            color={isAvailable ? 'black.cool' : 'grey.default'}>
            {product.name}
            {` • ${productUnitPriceFormatted(product)}`}
            {product.description ? (
              <>
                <Reference reference={reference} onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
                  <InformationIcon isDisabled={!isAvailable} ml='5px' />
                </Reference>
                <BodyPortal>
                  {isHovering ? (
                    <Tooltip popper={popper}>
                      <TooltipBox className='tooltip-box' onMouseOut={handleMouseOut}>
                        <Text
                          color='grey.default'
                          fontWeight={2}
                          fontSize='xxs'
                          mb='5px'
                          mt={0}
                          lineHeight={1.5}
                          css='letter-spacing:0.5px;text-transform:uppercase;'>
                          {product.name}
                        </Text>
                        <Text mb='5px' mt={0} fontSize='s' lineHeight={1.5}>
                          <div {...safelySetInnerHTML(product.description)} />
                        </Text>
                        <Text fontSize='s' fontStyle='italic' lineHeight={1.5}>
                          {t('shop.validity_duration', { duration: durationInWords(product.expired_after, locale) })}
                        </Text>
                      </TooltipBox>
                    </Tooltip>
                  ) : null}
                </BodyPortal>
              </>
            ) : null}
          </Text>
          <Text color='grey.default' fontStyle='italic'>
            {t('shop.validity_duration', { duration: durationInWords(product.expired_after, locale) })}
          </Text>
        </Box>
        {isAvailable ? (
          <Flex p={['10px']} pt={['0px', '10px']} width={['100%', 'auto']} onClick={() => addToCart(product.id)}>
            <Button testId='add-to-cart' size='small' hierarchy='primary-bold'>
              {t('shop.add_to_cart')}
            </Button>
          </Flex>
        ) : (
          <Text
            color='grey.default'
            mr='10px'
            ml={['0', '10px']}
            mb='10px'
            mt={['0', '10px']}
            lineHeight='20px'
            width={['100%', 'auto']}
            fontWeight='bold'
            textAlign='center'>
            {t('offers.not-available')}
          </Text>
        )}
      </Well>
    )

    function handleMouseOver() {
      hoverProduct(product.id)
      window.addEventListener('touchstart', handleMouseOut)
    }
    function handleMouseOut(e) {
      if (!(e.relatedTarget && e.relatedTarget.closest('.tooltip-box'))) {
        unhoverProduct()
        window.removeEventListener('touchstart', handleMouseOut)
      } else {
        e.relatedTarget.closest('.tooltip-box').addEventListener('mouseleave', handleMouseOut, { once: true })
      }
    }
  }
)

interface CartProductProps {
  product: Bookings.Product
  removeProduct: (productId: number) => void
  mini?: boolean
}

const CartProduct = observer(({ product, removeProduct, mini }: CartProductProps) => {
  const { appStore } = useContext(StoresContext)
  return (
    <TableRowBox key={`table-product${product.id}`} py='10px' pr='10px' color='black.cool'>
      <Flex flexDirection='row' justifyContent='flex-end' alignItems='center'>
        <Text mr='auto'>{product.name}</Text>
        <MoreLessInput
          fill='restaurant'
          testId={`${product.name}-quantity`}
          value={product.quantity}
          onMore={() => (product.quantity += 1)}
          onLess={() => {
            if (product.quantity === 1) {
              removeProduct(product.id)
            } else {
              product.quantity -= 1
            }
          }}
        />
        <Text width='60px' textAlign='center'>
          {appStore.state.productUnitPriceFormatted(product)}
        </Text>
        <Button
          iconOnly
          testId='remove-from-cart'
          ml='5px'
          hierarchy='secondary-subtler'
          size='small'
          px='padding.3'
          onClick={() => removeProduct(product.id)}>
          <Icon name='trash-bin' />
        </Button>
      </Flex>
    </TableRowBox>
  )
})

const Shop: NextPageWithLayout = observer((props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { appStore } = useContext(StoresContext)
  const state = useLocalObservable(() => ({ hoveringProduct: null }))
  const { mini } = appStore.state

  useEffect(() => {
    appStore.getProducts()
  }, [appStore])

  return (
    <Stack gap='gap.4' p='padding.4' data-color-mode='light'>
      <div>
        {appStore.state.products.map((product, index) => (
          <Product
            product={product}
            index={index}
            key={`product-${product.id}`}
            hoverProduct={hoverProduct}
            unhoverProduct={unhoverProduct}
            hoveringProduct={state.hoveringProduct}
            addToCart={addToCart}
          />
        ))}
      </div>
      {!mini && <Hr my='margin.4' />}
      {!mini && (
        <Text color='black.default' fontWeight={2} fontSize='xs' css='letter-spacing:0.5px;text-transform:uppercase;'>
          {t('shop.cart_title')}
        </Text>
      )}
      <TableBox>
        {appStore.state.selectedProducts.map((product) => (
          <CartProduct product={product} mini={mini} key={`product-${product.id}`} removeProduct={removeProduct} />
        ))}
        <TableRowBox
          bg='white.light'
          color='black.cool'
          borderRadius={appStore.state.selectedProducts.length ? '0 0 4px 4px' : '4px'}>
          <Flex flexDirection='row' justifyContent='flex-end'>
            <Text mr='auto' fontWeight='bold'>
              {t('shop.total')}
            </Text>
            <Text fontWeight='bold'> {appStore.state.cartPrice}</Text>
          </Flex>
        </TableRowBox>
      </TableBox>
      <Flex justifyContent='center'>
        <Button
          type='submit'
          hierarchy='primary-bold'
          size='medium'
          testId='go-to-form'
          disabled={appStore.state.isLoading || appStore.state.selectedProducts.length === 0}
          onClick={(e) => {
            goToForm()
            const isIE11 = !!(navigator.userAgent.match(/Trident/) && navigator.userAgent.match(/rv[ :]11/))
            if (isIE11) {
              e.preventDefault()
            }
          }}>
          {appStore.state.isLoading ? <Loader bounceColor='white.default' /> : t('shop.go_to_form')}
        </Button>
      </Flex>
    </Stack>
  )
  function addToCart(product_id) {
    const productInCart = appStore.state.selectedProducts.find((p) => p.id === product_id)
    if (productInCart) {
      productInCart.quantity++
    } else {
      const product = appStore.state.products.find((p) => p.id === product_id)
      product && appStore.state.selectedProducts.push({ ...product, quantity: 1 })
    }
  }

  function removeProduct(product_id) {
    const product = appStore.state.selectedProducts.find((p) => p.id === product_id)
    if (product) {
      appStore.state.selectedProducts.remove(product)
    }
  }

  function hoverProduct(productId) {
    state.hoveringProduct = productId
  }

  function unhoverProduct() {
    state.hoveringProduct = null
  }

  function goToForm() {
    const { pax, day, ...restQuery } = router.query
    router.push({
      pathname: '/shop-form',
      query: restQuery
    })
  }
})

Shop.getLayout = (page) => <MainLayout>{page}</MainLayout>

export default Shop
